import ConditionalWrapper from '@/components/ui/custom/ConditionalWrapper';
import { Menu } from '@/components/ui/daisy/menu';
import { forwardRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';
import TreeIcon from './TreeIcon';
import { FileTreeItemProps } from './types';

const DirectoryItem = forwardRef<HTMLLIElement, FileTreeItemProps>(
  (
    {
      label,
      classNames,
      icon,
      href,
      disabled = false,
      open,
      children,
      onToggle,
      onClick,
      ...props
    },
    ref,
  ) => {
    const summaryContent = (
      <ConditionalWrapper
        condition={!!href}
        wrapper={
          <Link
            to={href ?? ''}
            className="flex items-center gap-2 grow min-w-0"
          />
        }
        fallback={
          <span
            className="flex items-center gap-2 grow min-w-0"
            onClick={onClick}
          />
        }
      >
        <TreeIcon icon={icon} isOpen={!!open} />
        <span className={twMerge('min-w-0', classNames?.summary)}>{label}</span>
      </ConditionalWrapper>
    );

    return (
      <Menu.Dropdown
        ref={ref}
        label={summaryContent}
        open={!!open}
        onToggle={onToggle}
        classNames={{
          summary: 'flex items-center gap-2',
          li: twMerge('w-full', disabled && 'menu-disabled', classNames?.li),
          details: twMerge('w-full', classNames?.details),
        }}
        {...props}
      >
        {children}
      </Menu.Dropdown>
    );
  },
);

const LeafItem = forwardRef<HTMLLIElement, FileTreeItemProps>(
  (
    {
      classNames,
      label,
      icon,
      href,
      active = false,
      disabled = false,
      onClick,
    },
    ref,
  ) => {
    const leafIcon = <TreeIcon icon={icon} />;

    const handleOnClick = (e: React.MouseEvent<HTMLLIElement>) => {
      e.preventDefault();
      e.stopPropagation();
      onClick?.(e);
    };

    return (
      <Menu.Item
        ref={ref}
        disabled={disabled}
        className={classNames?.li}
        onClick={handleOnClick}
      >
        <ConditionalWrapper
          condition={!!href}
          wrapper={<Link to={href ?? ''} />}
        >
          <span
            className={twMerge(
              'flex grow gap-2 items-center w-full min-w-0',
              classNames?.summary,
              active && 'menu-active',
            )}
          >
            {leafIcon}
            <span className="truncate min-w-0">{label}</span>
          </span>
        </ConditionalWrapper>
      </Menu.Item>
    );
  },
);

const FileTreeItem = forwardRef<HTMLLIElement, FileTreeItemProps>(
  ({ defaultOpen = false, ...props }, ref) => {
    const { open, children, onToggle } = props;
    const isControlled = open !== undefined;
    const [internalOpen, setInternalOpen] = useState(defaultOpen);

    const isOpen = isControlled ? open : internalOpen;

    const handleToggle = (newOpen?: boolean) => {
      if (!isControlled) {
        setInternalOpen(prev => newOpen ?? !prev);
      }
      onToggle?.(newOpen);
    };

    if (!!children) {
      return (
        <DirectoryItem
          open={isOpen}
          onToggle={handleToggle}
          ref={ref}
          {...props}
        />
      );
    }

    return <LeafItem ref={ref} {...props} />;
  },
);

export default FileTreeItem;
